import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ConnectorSummary, type UserStructuredProps } from "../lib/api";

import { PlatformLogo } from "./PlatformLogo";
import {
  SourceForm,
  emptyValuesFromSchema,
  getMissingRequiredFields,
  type SourceFormValues,
} from "./SourceForm";

// useSchemaManagedSchedule derives isScheduled/scheduleCron/scheduleDescription
// straight from a schema-managed connector's own config fields (ingestion_mode,
// sync_frequency, sync_time, sync_timezone, sync_day, sync_date), and fills in
// sensible defaults for the newly-visible schedule fields as the operator
// switches modes/frequencies. Shared by any modal that renders a connector
// whose schema declares its own ingestion_mode toggle (Litmus Edge, PostgreSQL,
// SQL Server) instead of layering a synthetic one-time/scheduled UI on top.
export function useSchemaManagedSchedule(
  config: Record<string, unknown>,
  setValues: (updater: (prev: SourceFormValues) => SourceFormValues) => void,
) {
  const isScheduled = config["ingestion_mode"] === "scheduled";

  // Seed the same defaults every "synthetic" connector's SyncFrequencySection
  // starts from the moment scheduled mode is selected, since these fields are
  // driven straight from formValues.config here rather than through a
  // FieldRow's own mount-default-apply effect.
  useEffect(() => {
    if (!isScheduled) return;
    setValues((prev) => {
      const next = { ...prev.config };
      let changed = false;
      if (!next["sync_frequency"]) {
        next["sync_frequency"] = "monthly";
        changed = true;
      }
      if (!next["sync_time"]) {
        next["sync_time"] = "02:00";
        changed = true;
      }
      if (!next["sync_timezone"]) {
        next["sync_timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        changed = true;
      }
      return changed ? { ...prev, config: next } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScheduled]);

  useEffect(() => {
    if (!isScheduled) return;
    const freq = (config["sync_frequency"] as string) ?? "monthly";
    setValues((prev) => {
      if (freq === "monthly" && !prev.config["sync_date"])
        return { ...prev, config: { ...prev.config, sync_date: "1" } };
      if (freq === "weekly" && !prev.config["sync_day"])
        return { ...prev, config: { ...prev.config, sync_day: "Sunday" } };
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScheduled, config["sync_frequency"]]);

  // Now that the schedule fields are excluded from the schema handed to
  // SourceForm (so <SyncFrequencySection> can render them instead), SourceForm's
  // own "strip now-invisible fields" cleanup no longer reaches them. Replicate
  // that cleanup here so switching back to one-time mode still clears any
  // schedule values instead of leaving them stale in the saved config.
  useEffect(() => {
    if (isScheduled) return;
    setValues((prev) => {
      const keys = ["sync_frequency", "sync_date", "sync_day", "sync_time", "sync_timezone"];
      if (!keys.some((k) => k in prev.config)) return prev;
      const next = { ...prev.config };
      for (const k of keys) delete next[k];
      return { ...prev, config: next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScheduled]);

  const frequency = (config["sync_frequency"] as string) ?? "monthly";
  const time = (config["sync_time"] as string) ?? "02:00";
  const timezone = (config["sync_timezone"] as string) ?? "UTC";
  const day = (config["sync_day"] as string) ?? "Sunday";
  const date = (config["sync_date"] as string) ?? "1";

  const scheduleCron = isScheduled ? litmusCronFromConfig(frequency, time, timezone, day, date) : "";
  const scheduleDescription = isScheduled ? describeSchedule(frequency, time, timezone, day, date) : "";

  // setScheduleField lets callers drive a shared <SyncFrequencySection> straight
  // off formValues.config, the same way non-schema-managed connectors drive it
  // off their own local useState — so the widget itself is identical everywhere.
  function setScheduleField(key: string, value: string) {
    setValues((prev) => ({ ...prev, config: { ...prev.config, [key]: value } }));
  }

  return {
    isScheduled,
    scheduleCron,
    scheduleDescription,
    frequency,
    time,
    timezone,
    day,
    date,
    setScheduleField,
  };
}
import {
  isStructuredPropertiesValid,
  StructuredPropertiesEditor,
} from "./StructuredPropertiesEditor";
import { useToast } from "./toast/ToastProvider";

interface AddSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // When set, picking a connector from the grid fires this callback and closes
  // the dialog instead of proceeding to the inline configure step. Used by the
  // Sources page so it can open ConfigureConnectorModal for the selection.
  onConnectorSelected?: (connector: ConnectorSummary) => void;
  // When set, the picker step is skipped and the dialog opens straight on the
  // configure step for this connector. Used by the Connectors page where the
  // user has already chosen which connector they want.
  presetConnectorId?: string;
  // When set, the picker step is skipped AND the given fields are merged into
  // the form's initial values. Used by the Scanner page to pre-populate
  // host/port fields discovered via network probing.
  prefill?: {
    connectorId: string;
    fields: Record<string, string>;
    suggestedName?: string;
  };
}

// localTimeToUTC converts a HH:MM time in an IANA timezone to UTC hour+minute.
// Also returns dayOffset (-1, 0, or +1) for callers that need to adjust a day-of-week.
// Uses Intl to compute the offset without any third-party library.
function localTimeToUTC(
  h: number,
  m: number,
  timezone: string,
): { hour: number; min: number; dayOffset: number } {
  try {
    const now = new Date();
    // Create a Date at h:m UTC, then ask Intl what time that is in the target tz.
    // The difference gives us the UTC offset for that timezone right now.
    const ref = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m, 0),
    );
    const tzStr = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(ref);
    const parts = tzStr.split(":");
    const tzH = parseInt(parts[0] ?? "0", 10) % 24;
    const tzM = parseInt(parts[1] ?? "0", 10);
    // offset (minutes) = what UTC h:m shows as in target tz, minus h:m
    const offsetMin = tzH * 60 + tzM - (h * 60 + m);
    // local → UTC: subtract the offset; track whether it crosses midnight
    const rawUtcMin = h * 60 + m - offsetMin;
    const dayOffset = rawUtcMin < 0 ? -1 : rawUtcMin >= 1440 ? 1 : 0;
    const utcMin = ((rawUtcMin % 1440) + 1440) % 1440;
    return { hour: Math.floor(utcMin / 60), min: utcMin % 60, dayOffset };
  } catch {
    return { hour: h % 24, min: m, dayOffset: 0 }; // fallback: treat as UTC
  }
}

const DOW: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

// litmusCronFromConfig converts sync_frequency + sync_time + sync_timezone +
// sync_day + sync_date into a UTC cron expression the agent scheduler understands.
//
//   daily   + "17:25" + "Asia/Kolkata"                → "55 11 * * *"
//   weekly  + "02:00" + "UTC"          + "Sunday"     → "0 2 * * 0"
//   weekly  + "23:00" + "America/LA"   + "Friday"     → "0 7 * * 6"
//   monthly + "02:00" + "UTC"                  + "15" → "0 2 15 * *"
//   monthly + "02:00" + "UTC"                + "last" → "@monthly_last 0 2"
export function litmusCronFromConfig(
  frequency: string,
  time: string,
  timezone = "UTC",
  day = "Sunday",
  date = "1",
): string {
  const [hStr, mStr] = time.split(":");
  const h = parseInt(hStr ?? "2", 10);
  const m = parseInt(mStr ?? "0", 10);
  const localH = isNaN(h) ? 2 : h;
  const localM = isNaN(m) ? 0 : m;
  const { hour, min, dayOffset } = localTimeToUTC(localH, localM, timezone);
  if (frequency === "weekly") {
    const utcDow = ((((DOW[day] ?? 0) + dayOffset) % 7) + 7) % 7;
    return `${min} ${hour} * * ${utcDow}`;
  }
  if (frequency === "monthly") {
    if (date === "last") {
      return `@monthly_last ${min} ${hour}`;
    }
    const dateNum = parseInt(date, 10) || 1;
    const utcDate = dateNum + dayOffset;
    if (utcDate < 1 || utcDate > 28) {
      // Timezone shift pushed the date out of the safe 1-28 range → last day.
      return `@monthly_last ${min} ${hour}`;
    }
    return `${min} ${hour} ${utcDate} * *`;
  }
  return `${min} ${hour} * * *`;
}

// getTimezoneAbbr returns the short abbreviation (e.g. "PDT", "IST") for an
// IANA timezone at the given HH:MM local time, reflecting the current DST state.
function getTimezoneAbbr(timezone: string, time: string): string {
  try {
    const [hStr, mStr] = time.split(":");
    const h = parseInt(hStr ?? "0", 10);
    const m = parseInt(mStr ?? "0", 10);
    const now = new Date();
    const ref = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m, 0),
    );
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(ref);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
  } catch {
    return timezone;
  }
}

function toOrdinal(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
}

// describeSchedule returns a human-readable sentence describing when the ingestion
// will run, e.g. "Runs every Sunday at 02:00 PDT — the agent's America/Los_Angeles timezone."
export function describeSchedule(
  frequency: string,
  time: string,
  timezone: string,
  day: string,
  date: string,
): string {
  if (!time) return "";
  const tzAbbr = getTimezoneAbbr(timezone || "UTC", time);
  const tail = `— the agent's ${timezone || "UTC"} timezone.`;
  if (frequency === "daily") {
    return `Runs every day at ${time} ${tzAbbr} ${tail}`;
  }
  if (frequency === "weekly") {
    return `Runs every ${day || "Sunday"} at ${time} ${tzAbbr} ${tail}`;
  }
  if (frequency === "monthly") {
    const dateLabel =
      date === "last" ? "the last day" : `the ${toOrdinal(parseInt(date, 10) || 1)}`;
    return `Runs on ${dateLabel} of each month at ${time} ${tzAbbr} ${tail}`;
  }
  return "";
}

const SKELETON_KEYS = Array.from({ length: 9 }, (_, i) => `skel-${i}`);

interface ConnectorGridProps {
  connectors: ConnectorSummary[] | undefined;
  isLoading: boolean;
  search: string;
  onPick: (c: ConnectorSummary) => void;
}

function ConnectorGrid({ connectors, isLoading, search, onPick }: ConnectorGridProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {SKELETON_KEYS.map((k) => (
          <div key={k} className="h-24 rounded-lg border border-rule bg-paper animate-pulseSoft" />
        ))}
      </div>
    );
  }

  if (!connectors || connectors.length === 0) {
    return <p className="text-sm text-ash">No connectors are registered.</p>;
  }

  const filtered = connectors
    .filter(
      (c) =>
        c.displayName.toLowerCase().includes(search.toLowerCase()) ||
        c.id.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  if (filtered.length === 0) {
    return <p className="text-sm text-ash">No connectors match "{search}"</p>;
  }

  return (
    <div className="grid grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1 no-scrollbar">
      {filtered.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onPick(c)}
          className="flex flex-col items-center gap-2.5 rounded-lg border border-rule bg-surface px-3 py-4 text-center transition hover:border-litmus-dark-green-700/50 hover:bg-paper hover:shadow-sm select-none"
        >
          <PlatformLogo platformId={c.id} alt="" className="h-10 w-10 shrink-0" />
          <span className="text-[12px] font-semibold leading-tight text-ink line-clamp-2">
            {c.displayName}
          </span>
        </button>
      ))}
    </div>
  );
}

export function AddSourceDialog({
  open,
  onOpenChange,
  onConnectorSelected,
  presetConnectorId,
  prefill,
}: AddSourceDialogProps) {
  const qc = useQueryClient();
  const { show } = useToast();
  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.get<ConnectorSummary[]>("/api/v1/connectors"),
    enabled: open,
  });

  const [step, setStep] = useState<"pick" | "configure">("pick");
  const [picked, setPicked] = useState<ConnectorSummary | null>(null);
  const [connectorSearch, setConnectorSearch] = useState("");
  const [name, setName] = useState("");
  const [scheduleCron, setScheduleCron] = useState("");
  const [values, setValues] = useState<SourceFormValues>({
    config: {},
    secrets: {},
  });
  const [structuredProperties, setStructuredProperties] = useState<UserStructuredProps>({});
  const [error, setError] = useState<string | null>(null);

  const effectiveSchema = picked ? picked.schema : null;

  // Derive the cron expression automatically from sync_frequency + sync_time
  // when any connector is in "scheduled" mode.
  const isScheduledSync = values.config["ingestion_mode"] === "scheduled";

  // When the user switches sync_frequency, SourceForm's cleanup effect removes
  // the now-invisible field (e.g. sync_day → monthly, sync_date → weekly) using
  // a non-functional setValues. Because cleanup runs in the same React batch as
  // FieldRow's default-apply, the cleanup overwrites the default before it lands.
  // Using a functional update here guarantees we read the post-cleanup state and
  // re-apply the correct default for the newly visible field.
  useEffect(() => {
    if (!isScheduledSync) return;
    if (values.config["sync_timezone"]) return; // already set
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setValues((prev: SourceFormValues) => ({
      ...prev,
      config: { ...prev.config, sync_timezone: detected },
    }));
  }, [isScheduledSync]);

  // Ensure sync_date / sync_day defaults are set when frequency changes.
  useEffect(() => {
    if (!isScheduledSync) return;
    const freq = (values.config["sync_frequency"] as string) ?? "daily";
    setValues((prev: SourceFormValues) => {
      if (freq === "monthly" && !prev.config["sync_date"])
        return { ...prev, config: { ...prev.config, sync_date: "1" } };
      if (freq === "weekly" && !prev.config["sync_day"])
        return { ...prev, config: { ...prev.config, sync_day: "Sunday" } };
      return prev;
    });
  }, [isScheduledSync, values.config["sync_frequency"]]);

  useEffect(() => {
    if (!isScheduledSync) {
      // Clear any previously-computed schedule when the user switches away.
      setScheduleCron("");
      return;
    }
    const freq = (values.config["sync_frequency"] as string) ?? "daily";
    const time = (values.config["sync_time"] as string) ?? "02:00";
    const tz = (values.config["sync_timezone"] as string) ?? "UTC";
    const day = (values.config["sync_day"] as string) ?? "Sunday";
    const date = (values.config["sync_date"] as string) ?? "1";
    setScheduleCron(litmusCronFromConfig(freq, time, tz, day, date));
  }, [
    isScheduledSync,
    values.config["sync_frequency"],
    values.config["sync_time"],
    values.config["sync_timezone"],
    values.config["sync_day"],
    values.config["sync_date"],
  ]);

  // When the parent supplied a preset connector (or a prefill with a connector
  // ID), skip the picker once the connector list has loaded. If prefill fields
  // are provided, merge them into the form's initial config values.
  const effectiveConnectorId = prefill?.connectorId ?? presetConnectorId;
  useEffect(() => {
    if (!open || !effectiveConnectorId || picked || !connectors.data) return;

    const found = connectors.data.find((c) => c.id === effectiveConnectorId);

    if (found) {
      const base = emptyValuesFromSchema(found.schema);
      const merged: SourceFormValues = prefill?.fields
        ? {
            ...base,
            config: { ...base.config, ...prefill.fields },
          }
        : base;
      setPicked(found);
      setValues(merged);
      setStep("configure");

      if (prefill?.suggestedName) {
        setName(prefill.suggestedName);
      } else if (prefill?.fields) {
        const host = prefill.fields.hostname ?? prefill.fields.host ?? "";
        if (host) setName(`${found.id}-${host}`);
      }
    }
  }, [open, effectiveConnectorId, connectors.data, picked, prefill]);

  function reset() {
    setStep("pick");
    setPicked(null);
    setName("");
    setScheduleCron("");
    setValues({ config: {}, secrets: {} });
    setStructuredProperties({});
    setError(null);
    setConnectorSearch("");
  }

  function handleClose() {
    cleanupStagedUploads();
    reset();
    onOpenChange(false);
  }

  // Best-effort cleanup of any FieldFile uploads the operator made but
  // never saved (closed the dialog, hit Cancel, picked a different
  // connector). The orphan janitor would catch these eventually anyway,
  // but firing the DELETE now keeps the data dir tidy.
  function cleanupStagedUploads() {
    if (!picked) return;

    for (const f of picked.schema.fields) {
      if (f.type !== "file") continue;

      const v = values.config[f.key];

      if (typeof v === "string" && v) {
        fetch(`/api/v1/uploads/${v}`, {
          method: "DELETE",
          credentials: "same-origin",
        }).catch(() => {
          /* janitor will pick up anything we miss */
        });
      }
    }
  }

  const create = useMutation({
    mutationFn: (body: unknown) => api.post("/api/v1/sources", body),

    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      show(
        "Source added",
        name.trim() ? `"${name.trim()}" is ready` : "New source created",
        "success",
      );
      reset();
      onOpenChange(false);
    },

    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Create failed";
      setError(msg);
      show("Create failed", msg, "error");
    },
  });

  const scheduleDescription = isScheduledSync
    ? describeSchedule(
        (values.config["sync_frequency"] as string) ?? "daily",
        (values.config["sync_time"] as string) ?? "02:00",
        (values.config["sync_timezone"] as string) ?? "UTC",
        (values.config["sync_day"] as string) ?? "Sunday",
        (values.config["sync_date"] as string) ?? "1",
      )
    : "";

  function submit() {
    if (!picked) return;

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }

    const missing = getMissingRequiredFields(picked.schema, values, false);
    if (missing.length > 0) {
      setError(`Required: ${missing.join(", ")}`);
      return;
    }

    if (!isStructuredPropertiesValid(structuredProperties)) {
      setError("Fix the invalid or conflicting static metadata fields before continuing.");
      return;
    }

    setError(null);

    create.mutate({
      name: name.trim(),
      connectorId: picked.id,
      config: values.config,
      secrets: values.secrets,
      structuredProperties,
      scheduleCron: scheduleCron.trim(),
      enabled: true,
    });
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1400] bg-ink/40 backdrop-blur-sm" />

        <Dialog.Content
          className="
            fixed
            left-1/2
            top-[72px]
            z-[1401]
            w-full
            max-w-xl
            -translate-x-1/2
            rounded-soft
            border
            border-rule
            bg-surface
            p-7
            shadow-cardLift
            max-h-[calc(100vh-96px)]
            overflow-y-auto
            no-scrollbar
          "
        >
          <Dialog.Title asChild>
            <h2 className="font-display text-[22px] font-semibold tracking-tight text-ink">
              {step === "pick" ? "Add a source" : `Configure ${picked?.displayName}`}
            </h2>
          </Dialog.Title>

          <Dialog.Description className="mt-1.5 mb-6 text-[13px] text-ash">
            {step === "pick"
              ? "Pick a connector. The next step's form is generated from its schema."
              : isScheduledSync
                ? "🛡 Secrets are AES-256-GCM encrypted at rest."
                : "Fill in the connection details. Secrets are AES-256-GCM encrypted at rest."}
          </Dialog.Description>

          {step === "pick" ? (
            <div className="space-y-4">
              <input
                type="search"
                value={connectorSearch}
                onChange={(e) => setConnectorSearch(e.target.value)}
                placeholder="Search connectors…"
                className="input"
                autoFocus
              />

              <ConnectorGrid
                connectors={connectors.data}
                isLoading={connectors.isLoading}
                search={connectorSearch}
                onPick={(c) => {
                  if (onConnectorSelected) {
                    onOpenChange(false);
                    onConnectorSelected(c);
                  } else {
                    setPicked(c);
                    setValues(emptyValuesFromSchema(c.schema));
                    setStep("configure");
                  }
                }}
              />
            </div>
          ) : null}

          {step === "configure" && picked ? (
            <div className="space-y-5">
              <label className="block">
                <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                  Name
                </span>

                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={`${picked.displayName} on plant-01`}
                  className="input"
                />
              </label>

              <SourceForm
                schema={effectiveSchema ?? picked.schema}
                value={values}
                onChange={setValues}
                connectorId={picked.id}
              />

              <StructuredPropertiesEditor
                value={structuredProperties}
                onChange={setStructuredProperties}
              />

              {scheduleDescription ? (
                <p className="text-[12.5px] leading-relaxed text-ash">{scheduleDescription}</p>
              ) : null}

              {error ? (
                <div className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                  {error}
                </div>
              ) : null}

              <div className="flex justify-between border-t border-rule pt-5">
                <button
                  onClick={() => {
                    setStep("pick");
                    setConnectorSearch("");
                  }}
                  className="text-[12px] text-ash hover:text-ink"
                >
                  ← Pick a different connector
                </button>

                <div className="flex gap-2">
                  <button onClick={handleClose} className="btn-secondary">
                    Cancel
                  </button>

                  <button onClick={submit} disabled={create.isPending} className="btn-primary">
                    {create.isPending
                      ? "Creating…"
                      : isScheduledSync
                        ? "Create scheduled source →"
                        : "Create source →"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
